import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { SingleTrainFormSchema } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { GroupedInformationResponse } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useInfrastructureInformationForTrain";
import {
  InfrastructureConsequence,
  mapInfrastructureConsequence,
} from "features/VehicleSidebar/AffectedTrains/utils/utils";
import { TFunction } from "i18next";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { UseFormSetValue, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Select } from "shared/components/forms/Select";
import { Checkbox } from "shared/components/Spor";
import { getTrainCustomInputs } from "shared/types/form";
import { isPlannedInfraStructureEvent } from "shared/utils/infrastructureEvent";

const mapKeysToLabel = (
  translate: TFunction<"translation", undefined>,
): Record<keyof GroupedInformationResponse, string> => ({
  directlyAffecting: translate("Påvirker direkte"),
  rest: translate("Øvrige"),
});

type NarrowedGroup = InfrastructureResponse & { incidentId: string };

function mapToOptions(
  groupedInformation: GroupedInformationResponse,
  translate: TFunction<"translation", undefined>,
): Option[] {
  return Object.keys(groupedInformation)
    .map((key) => {
      const typedKey = key as keyof GroupedInformationResponse;
      if (groupedInformation[typedKey].length === 0) {
        return null;
      }
      return {
        label: mapKeysToLabel(translate)[typedKey],
        selection: groupedInformation[typedKey]
          .filter((group): group is NarrowedGroup => group.incidentId !== null)
          .map((group) => {
            let displayValue = group.stretchName;

            if (isPlannedInfraStructureEvent(group.type)) {
              displayValue = `${displayValue} ${translate("(planlagt)")}`;
            }

            return {
              id: group.uuid,
              value: group.incidentId,
              displayValue,
            };
          }),
      };
    })
    .filter((opt: Option | null): opt is Option => opt !== null);
}

type Option = {
  label: string;
  selection: {
    id: string;
    value: string;
    displayValue: string;
  }[];
};

function buildOptGroups(...options: Option[]) {
  return (
    <>
      {options.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.selection.map((opt) => (
            <option key={opt.id} value={opt.value}>
              {opt.displayValue}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

type IncidentsForTrainProps = {
  items: GroupedInformationResponse;
};

const handleIncidentSelectChange = ({
  selectedId,
  infrastructureData,
  setValue,
  translate,
}: {
  selectedId: string;
  infrastructureData: GroupedInformationResponse;
  setValue: UseFormSetValue<SingleTrainFormSchema>;
  translate: TFunction<"translation", undefined>;
}) => {
  const flattenedInfrastructureData = [
    ...infrastructureData.directlyAffecting,
    ...infrastructureData.rest,
  ];

  const selectedInfrastructureData = flattenedInfrastructureData.find(
    (val) => val.incidentId === selectedId,
  );

  if (!selectedInfrastructureData) {
    return;
  }

  const { consequence, reason, action } = selectedInfrastructureData;

  const trainCustomInput = getTrainCustomInputs(translate);
  const updates = [
    {
      name: trainCustomInput.event.typeFormField,
      value: mapInfrastructureConsequence(
        consequence?.type as InfrastructureConsequence | undefined,
      ),
    },
    {
      name: trainCustomInput.reason.typeFormField,
      value: reason?.type ?? "",
    },
    {
      name: trainCustomInput.action.typeFormField,
      value: action?.type ?? "",
    },
  ];

  updates.forEach(({ name, value }) => {
    setValue(name, value);
  });
};

export const SelectIncidentsForTrain: FC<IncidentsForTrainProps> = ({
  items,
}) => {
  const { t } = useTranslation();
  const formField = "trainForm.incidentId";

  const { register, setValue, getValues } =
    useFormContext<SingleTrainFormSchema>();

  const displayIncidents = buildOptGroups(...mapToOptions(items, t));

  const incidentId = getValues(formField);

  const [showIncidentsCombobox, setShowIncidentsCombobox] = useState(
    () => !!incidentId, // Possibly true when opening an edit modal
  );

  useEffect(() => {
    if (incidentId && !showIncidentsCombobox) {
      setShowIncidentsCombobox(true);
    }
  });

  return (
    <>
      <Checkbox
        checked={showIncidentsCombobox}
        onCheckedChange={() => {
          if (showIncidentsCombobox) {
            // Toggling off the incidents box, empty the field too
            setValue("trainForm.incidentId", "", { shouldValidate: true });
          }
          setShowIncidentsCombobox(!showIncidentsCombobox);
        }}
      >
        {t("Knytt hendelse til infrastrukturhendelse")}
      </Checkbox>
      {showIncidentsCombobox && (
        <Select
          data-testid="select_Infrastrukturhendelse"
          label={t("Infrastrukturhendelse")}
          placeholder={t("Velg en infrastrukturhendelse")}
          {...register(formField, {
            onChange: (e: ChangeEvent<HTMLSelectElement>) => {
              setValue(formField, e.target.value);
              handleIncidentSelectChange({
                selectedId: e.target.value,
                infrastructureData: items,
                setValue,
                translate: t,
              });
            },
          })}
          value={incidentId ?? ""}
        >
          {displayIncidents}
        </Select>
      )}
    </>
  );
};
